import { useState } from 'react';
import { Card, CardContent } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Textarea } from '@/shared/ui/textarea';
import { Button } from '@/shared/ui/button';
import { ArrowUp } from 'lucide-react';
import { useToast } from '@/shared/hooks/use-toast';
import { CONTACT_INFO, FOOTER_NAV_LINKS, FOOTER_LEGAL_LINKS } from './constants';
import { IronviewLogo } from '@/shared/components/IronviewLogo';

interface ContactFormSectionProps {
  onNavigate: (sectionId: string) => void;
  onBackToTop: () => void;
}

const ContactFormSection: React.FC<ContactFormSectionProps> = ({ onNavigate, onBackToTop }) => {
  const { toast } = useToast();
  const [contactFormData, setContactFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const payload: {
        name: string;
        email: string;
        message: string;
        phone?: string;
      } = {
        name: contactFormData.name,
        email: contactFormData.email,
        message: contactFormData.message,
      };

      if (contactFormData.phone.trim()) {
        payload.phone = contactFormData.phone;
      }

      const response = await fetch('/api/v1/support/contact/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...payload,
          source: 'homepage_contact',
          subject: 'Website contact request',
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast({
          title: 'Success',
          description:
            data.message ||
            'Your message has been sent successfully. We will get back to you within 24 hours.',
          className: 'bg-white text-black',
        });
        setContactFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        throw new Error(data.message || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error sending contact form:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description:
          error instanceof Error
            ? error.message
            : 'Failed to send message. Please try again later.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer id="contact-us" role="contentinfo" className={`relative min-h-screen bg-maintenance-navy`}>
      <button
        onClick={onBackToTop}
        className={`absolute top-2 md:top-4 left-1/2 transform -translate-x-1/2 text-white/50 hover:text-white transition-colors z-10`}
      >
        <ArrowUp className="w-8 h-8 md:w-12 md:h-12" />
      </button>

      <div className="container mx-auto px-4 md:px-6 py-8 md:py-16">
        <div className="flex flex-col lg:flex-row gap-8 md:gap-12 items-start justify-between max-w-7xl mx-auto">
          {/* Left Side — Logo and Contact Info */}
          <div className="flex flex-col items-start w-full lg:w-1/3">
            <div className="rounded-lg mb-5 px-3 py-2 inline-block">
              <IronviewLogo
                tone="light"
                className="w-48 sm:w-56 md:w-64 lg:w-72"
              />
            </div>

            <address className="not-italic mb-6 md:mb-8">
              <h2 className={`text-xl md:text-2xl font-bold text-white mb-3 md:mb-4`}>Contact:</h2>
              <div className={`text-base md:text-lg space-y-1 md:space-y-2`}>
                <a
                  href={`mailto:${CONTACT_INFO.email}`}
                  className={`block break-words text-white/70 hover:text-white transition-colors`}
                >
                  {CONTACT_INFO.email}
                </a>
                <a
                  href={`tel:${CONTACT_INFO.phone.replace(/\./g, '')}`}
                  className={`block text-white/70 hover:text-white transition-colors`}
                >
                  {CONTACT_INFO.phone}
                </a>
              </div>
            </address>

            <nav className={`space-y-1 md:space-y-2 text-sm md:text-base mb-6 md:mb-0`}>
              <a
                href="/support"
                className="block text-left text-white/70 transition-colors hover:text-white"
              >
                Support
              </a>
              {FOOTER_NAV_LINKS.map((link) => (
                <button
                  key={link.id}
                  onClick={() => onNavigate(link.id)}
                  className={`block text-white/70 hover:text-white transition-colors text-left`}
                >
                  {link.label}
                </button>
              ))}
            </nav>

            <div className={`mt-6 md:mt-8 text-sm md:text-base space-y-1 md:space-y-2`}>
              {FOOTER_LEGAL_LINKS.map((link) => (
                link.id === 'privacy' ? (
                  <a
                    key={link.id}
                    href="/privacy"
                    className="block text-left text-white/50 transition-colors hover:text-white"
                  >
                    {link.label}
                  </a>
                ) : (
                  <button
                    key={link.id}
                    className="block text-left text-white/50 transition-colors hover:text-white"
                  >
                    {link.label}
                  </button>
                )
              ))}
            </div>

            <p className={`text-sm md:text-base text-white/50 mt-6 md:mt-8`}>
              {CONTACT_INFO.copyright}
            </p>
          </div>

          {/* Right Side — Contact Form */}
          <div className="w-full lg:w-2/3">
            <Card className={`bg-maintenance-slate/90 border border-white/10 text-white shadow-xl rounded-xl`}>
              <CardContent className="p-4 sm:p-6 md:p-8">
                <h2 className={`text-2xl sm:text-3xl font-bold text-white mb-4 sm:mb-6`}>
                  Contact Us
                </h2>
                <form onSubmit={handleContactSubmit} className="space-y-4 sm:space-y-6">
                  <div>
                    <Label htmlFor="name" className={`text-white/70 text-base md:text-lg mb-2 block`}>
                      Full Name *
                    </Label>
                    <Input
                      id="name"
                      type="text"
                      placeholder="Enter your full name"
                      value={contactFormData.name}
                      onChange={(e) =>
                        setContactFormData({ ...contactFormData, name: e.target.value })
                      }
                      required
                      disabled={isSubmitting}
                      className={`bg-maintenance-dark/60 border-white/20 text-white placeholder:text-white/30 focus:border-white/60 focus-visible:ring-0 text-sm md:text-base`}
                    />
                  </div>

                  <div>
                    <Label htmlFor="contact_email" className={`text-white/70 text-base md:text-lg mb-2 block`}>
                      Email Address *
                    </Label>
                    <Input
                      id="contact_email"
                      type="email"
                      placeholder="your@email.com"
                      value={contactFormData.email}
                      onChange={(e) =>
                        setContactFormData({ ...contactFormData, email: e.target.value })
                      }
                      required
                      disabled={isSubmitting}
                      className={`bg-maintenance-dark/60 border-white/20 text-white placeholder:text-white/30 focus:border-white/60 focus-visible:ring-0 text-sm md:text-base`}
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" className={`text-white/70 text-base md:text-lg mb-2 block`}>
                      Phone Number
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="Your phone number"
                      value={contactFormData.phone}
                      onChange={(e) =>
                        setContactFormData({ ...contactFormData, phone: e.target.value })
                      }
                      disabled={isSubmitting}
                      className={`bg-maintenance-dark/60 border-white/20 text-white placeholder:text-white/30 focus:border-white/60 focus-visible:ring-0 text-sm md:text-base`}
                    />
                  </div>

                  <div>
                    <Label htmlFor="message" className={`text-white/70 text-base md:text-lg mb-2 block`}>
                      Message *
                    </Label>
                    <Textarea
                      id="message"
                      placeholder="Tell us how we can help you..."
                      value={contactFormData.message}
                      onChange={(e) =>
                        setContactFormData({ ...contactFormData, message: e.target.value })
                      }
                      required
                      disabled={isSubmitting}
                      rows={6}
                      className={`bg-maintenance-dark/60 border-white/20 text-white placeholder:text-white/30 focus:border-white/60 focus-visible:ring-0 resize-none text-sm md:text-base`}
                    />
                  </div>

                  <div className="flex justify-center">
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className={`bg-maintenance-navy text-white hover:bg-maintenance-blue border border-white/30 hover:border-white transition-colors px-8 sm:px-12 py-2 sm:py-3 text-base sm:text-lg font-semibold w-full sm:w-auto`}
                    >
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default ContactFormSection;
